import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Loader2, Save, Settings, Image as ImageIcon, Mail, Upload, X, 
  Shield, CheckCircle, AlertCircle, Send, Info, MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LOGIN_TYPES = [
  {
    value: 'domain',
    label: 'Domain',
    description: 'Standard domain-based authentication. Users log in with their domain credentials.',
    icon: '🏢',
    recommended: false
  },
  {
    value: 'token',
    label: 'Token',
    description: 'Token-based authentication. Users authenticate using Mi-Token credentials.',
    icon: '🔑',
    recommended: true
  },
  {
    value: 'adaptive',
    label: 'Adaptive',
    description: 'Adaptive authentication. The system automatically determines the best authentication method based on user context.',
    icon: '🤖',
    recommended: false
  },
  {
    value: 'mixed',
    label: 'Mixed',
    description: 'Mixed mode authentication. Users can choose between domain or token-based authentication.',
    icon: '🔀',
    recommended: false
  }
];

export const SettingsPage = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('branding');
  const [loginType, setLoginType] = useState('domain');
  const [adminLogoUrl, setAdminLogoUrl] = useState('');
  const [userLogoUrl, setUserLogoUrl] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFromEmail, setSmtpFromEmail] = useState('');
  const [smtpFromName, setSmtpFromName] = useState('');
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [smsProvider, setSmsProvider] = useState('');
  const [smsAccountSid, setSmsAccountSid] = useState('');
  const [smsAuthToken, setSmsAuthToken] = useState('');
  const [smsFromNumber, setSmsFromNumber] = useState('');
  const [mitokenApiServer, setMitokenApiServer] = useState('');
  const [mitokenApiKey, setMitokenApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [showTestEmailDialog, setShowTestEmailDialog] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [loginType, adminLogoUrl, userLogoUrl, smtpHost, smtpPort, smtpUsername, smtpPassword, smtpFromEmail, smtpFromName, smtpUseTls, smsProvider, smsAccountSid, smsAuthToken, smsFromNumber, mitokenApiServer, mitokenApiKey]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setLoginType(response.data.login_type);
      setAdminLogoUrl(response.data.admin_logo_url || '');
      setUserLogoUrl(response.data.user_logo_url || '');
      setSmtpHost(response.data.smtp_host || '');
      setSmtpPort(response.data.smtp_port || '587');
      setSmtpUsername(response.data.smtp_username || '');
      setSmtpPassword(response.data.smtp_password || '');
      setSmtpFromEmail(response.data.smtp_from_email || '');
      setSmtpFromName(response.data.smtp_from_name || '');
      setSmtpUseTls(response.data.smtp_use_tls !== false);
      setSmsProvider(response.data.sms_provider || '');
      setSmsAccountSid(response.data.sms_account_sid || '');
      setSmsAuthToken(response.data.sms_auth_token || '');
      setSmsFromNumber(response.data.sms_from_number || '');
      setMitokenApiServer(response.data.mitoken_api_server || '');
      setMitokenApiKey(response.data.mitoken_api_key || '');
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (file, type) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG, PNG, GIF, WEBP, or SVG)');
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64String = e.target.result;
      if (type === 'admin') {
        setAdminLogoUrl(base64String);
        toast.success('Admin logo uploaded successfully');
      } else {
        setUserLogoUrl(base64String);
        toast.success('User logo uploaded successfully');
      }
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${BACKEND_URL}/api/admin/settings`,
        { 
          login_type: loginType,
          admin_logo_url: adminLogoUrl,
          user_logo_url: userLogoUrl,
          smtp_host: smtpHost,
          smtp_port: parseInt(smtpPort),
          smtp_username: smtpUsername,
          smtp_password: smtpPassword,
          smtp_from_email: smtpFromEmail,
          smtp_from_name: smtpFromName,
          smtp_use_tls: smtpUseTls,
          sms_provider: smsProvider,
          sms_account_sid: smsAccountSid,
          sms_auth_token: smsAuthToken,
          sms_from_number: smsFromNumber,
          mitoken_api_server: mitokenApiServer,
          mitoken_api_key: mitokenApiKey
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      toast.success('Settings saved successfully');
      setHasUnsavedChanges(false);
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = () => {
    if (!smtpHost || !smtpPort || !smtpFromEmail) {
      toast.error('Please fill in SMTP Host, Port, and From Email before testing');
      return;
    }
    setShowTestEmailDialog(true);
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress || !testEmailAddress.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setTestingSmtp(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/settings/test-email`,
        { 
          recipient_email: testEmailAddress
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('adminToken')}`
          }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || 'Test email sent successfully! Check your inbox.');
        setShowTestEmailDialog(false);
        setTestEmailAddress('');
      } else {
        toast.error(response.data.message || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Test email error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email. Please check your SMTP settings.');
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleTestSms = async () => {
    if (!smsProvider || !smsFromNumber) {
      toast.error('Please fill in SMS provider and from number');
      return;
    }

    setTestingSms(true);
    try {
      // Simulate SMS test
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success('SMS configuration test successful! Test message would be sent.');
    } catch (error) {
      toast.error('SMS test failed. Please check your configuration.');
    } finally {
      setTestingSms(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Settings className="h-8 w-8 text-slate-700" />
            <h1 className="text-3xl font-outfit font-bold text-slate-900">{t('admin.settings.title')}</h1>
          </div>
          <p className="text-slate-600 font-ibm">{t('admin.settings.subtitle')}</p>
        </div>
        {hasUnsavedChanges && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            Unsaved Changes
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-flex">
          <TabsTrigger value="branding" className="font-ibm">
            <ImageIcon className="h-4 w-4 mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="smtp" className="font-ibm">
            <Mail className="h-4 w-4 mr-2" />
            Email (SMTP)
          </TabsTrigger>
          <TabsTrigger value="sms" className="font-ibm">
            <MessageSquare className="h-4 w-4 mr-2" />
            SMS
          </TabsTrigger>
          <TabsTrigger value="mitoken" className="font-ibm">
            <Settings className="h-4 w-4 mr-2" />
            Mi-Token API
          </TabsTrigger>
          <TabsTrigger value="authentication" className="font-ibm">
            <Shield className="h-4 w-4 mr-2" />
            Authentication
          </TabsTrigger>
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit">Logo Configuration</CardTitle>
              <CardDescription className="font-ibm">
                Customize the logos displayed in your admin and user portals
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Admin Logo */}
              <div className="space-y-4 p-6 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <Label className="text-lg font-ibm font-semibold text-slate-900">
                    Admin Portal Logo
                  </Label>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="adminLogoUrl" className="text-sm font-ibm text-slate-700">
                      <Info className="h-3 w-3 inline mr-1" />
                      Enter Image URL
                    </Label>
                    <Input
                      id="adminLogoUrl"
                      type="url"
                      value={adminLogoUrl.startsWith('data:') ? '' : adminLogoUrl}
                      onChange={(e) => setAdminLogoUrl(e.target.value)}
                      placeholder="https://example.com/admin-logo.png"
                      className="font-ibm"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="adminLogoFile" className="text-sm font-ibm text-slate-700">
                      <Upload className="h-3 w-3 inline mr-1" />
                      Or Upload Image
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="adminLogoFile"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files[0], 'admin')}
                        className="font-ibm"
                      />
                      {adminLogoUrl && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setAdminLogoUrl('')}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 font-ibm">
                      Max 2MB • JPG, PNG, GIF, WEBP, SVG
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-ibm text-slate-600 dark:text-slate-300">
                      {adminLogoUrl && adminLogoUrl.trim() !== '' ? 'Preview' : 'Current Logo'}
                    </span>
                    {adminLogoUrl && adminLogoUrl.startsWith('data:') && (
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Uploaded
                      </Badge>
                    )}
                    {(!adminLogoUrl || adminLogoUrl.trim() === '') && (
                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                        Default
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-center bg-slate-50 dark:bg-slate-900 rounded p-2">
                    <img 
                      src={adminLogoUrl && adminLogoUrl.trim() !== '' ? adminLogoUrl : 'https://customer-assets.emergentagent.com/job_id-selfservice/artifacts/tfwsm7zn_Logo%2BNuevo%2BMi-Token.png.webp'} 
                      alt="Admin Logo Preview" 
                      className="h-16 object-contain"
                      onError={(e) => {
                        console.error('Failed to load admin logo');
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* User Logo */}
              <div className="space-y-4 p-6 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <Label className="text-lg font-ibm font-semibold text-slate-900">
                    User Portal Logo
                  </Label>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="userLogoUrl" className="text-sm font-ibm text-slate-700">
                      <Info className="h-3 w-3 inline mr-1" />
                      Enter Image URL
                    </Label>
                    <Input
                      id="userLogoUrl"
                      type="url"
                      value={userLogoUrl.startsWith('data:') ? '' : userLogoUrl}
                      onChange={(e) => setUserLogoUrl(e.target.value)}
                      placeholder="https://example.com/user-logo.png"
                      className="font-ibm"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="userLogoFile" className="text-sm font-ibm text-slate-700">
                      <Upload className="h-3 w-3 inline mr-1" />
                      Or Upload Image
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="userLogoFile"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files[0], 'user')}
                        className="font-ibm"
                      />
                      {userLogoUrl && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setUserLogoUrl('')}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 font-ibm">
                      Max 2MB • JPG, PNG, GIF, WEBP, SVG
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-ibm text-slate-600 dark:text-slate-300">
                      {userLogoUrl && userLogoUrl.trim() !== '' ? 'Preview' : 'Current Logo'}
                    </span>
                    {userLogoUrl && userLogoUrl.startsWith('data:') && (
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Uploaded
                      </Badge>
                    )}
                    {(!userLogoUrl || userLogoUrl.trim() === '') && (
                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                        Default
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-center bg-slate-50 dark:bg-slate-900 rounded p-2">
                    <img 
                      src={userLogoUrl && userLogoUrl.trim() !== '' ? userLogoUrl : 'https://customer-assets.emergentagent.com/job_id-selfservice/artifacts/tfwsm7zn_Logo%2BNuevo%2BMi-Token.png.webp'} 
                      alt="User Logo Preview" 
                      className="h-16 object-contain"
                      onError={(e) => {
                        console.error('Failed to load user logo');
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMTP Tab */}
        <TabsContent value="smtp" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-outfit">SMTP Gateway Configuration</CardTitle>
                  <CardDescription className="font-ibm">
                    Configure email server settings for sending notifications
                  </CardDescription>
                </div>
                <Button
                  onClick={handleTestSmtp}
                  disabled={testingSmtp}
                  variant="outline"
                  size="sm"
                  className="font-ibm"
                >
                  {testingSmtp ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Test SMTP
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="smtpHost" className="font-ibm font-medium">
                    SMTP Host <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="smtpHost"
                    type="text"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    placeholder="smtp.gmail.com"
                    className="font-ibm"
                  />
                  <p className="text-xs text-slate-500 font-ibm">
                    Example: smtp.gmail.com, smtp-mail.outlook.com
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtpPort" className="font-ibm font-medium">
                    SMTP Port <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="smtpPort"
                    type="number"
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(e.target.value)}
                    placeholder="587"
                    className="font-ibm"
                  />
                  <p className="text-xs text-slate-500 font-ibm">
                    Common: 587 (TLS), 465 (SSL), 25 (unencrypted)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="smtpUsername" className="font-ibm font-medium">
                    SMTP Username
                  </Label>
                  <Input
                    id="smtpUsername"
                    type="text"
                    value={smtpUsername}
                    onChange={(e) => setSmtpUsername(e.target.value)}
                    placeholder="username@example.com"
                    className="font-ibm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtpPassword" className="font-ibm font-medium">
                    SMTP Password
                  </Label>
                  <Input
                    id="smtpPassword"
                    type="password"
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    placeholder="••••••••"
                    className="font-ibm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="smtpFromEmail" className="font-ibm font-medium">
                    From Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="smtpFromEmail"
                    type="email"
                    value={smtpFromEmail}
                    onChange={(e) => setSmtpFromEmail(e.target.value)}
                    placeholder="noreply@example.com"
                    className="font-ibm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtpFromName" className="font-ibm font-medium">
                    From Name
                  </Label>
                  <Input
                    id="smtpFromName"
                    type="text"
                    value={smtpFromName}
                    onChange={(e) => setSmtpFromName(e.target.value)}
                    placeholder="Mi-Token System"
                    className="font-ibm"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  id="smtpUseTls"
                  checked={smtpUseTls}
                  onChange={(e) => setSmtpUseTls(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-emerald focus:ring-emerald"
                />
                <Label htmlFor="smtpUseTls" className="font-ibm font-medium cursor-pointer">
                  Use TLS/SSL Encryption (Recommended)
                </Label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-ibm">
                  <strong>💡 Tip:</strong> For Gmail, you may need to use an &quot;App Password&quot; instead of your regular password. 
                  Generate one in your Google Account security settings.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMS Tab */}
        <TabsContent value="sms" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-outfit">TEXT/SMS Gateway Configuration</CardTitle>
                  <CardDescription className="font-ibm">
                    Configure SMS provider settings for sending text messages
                  </CardDescription>
                </div>
                <Button
                  onClick={handleTestSms}
                  disabled={testingSms}
                  variant="outline"
                  size="sm"
                  className="font-ibm"
                >
                  {testingSms ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Test SMS
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="smsProvider" className="font-ibm font-medium">
                    SMS Provider <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="smsProvider"
                    type="text"
                    value={smsProvider}
                    onChange={(e) => setSmsProvider(e.target.value)}
                    placeholder="Twilio, AWS SNS, etc."
                    className="font-ibm"
                  />
                  <p className="text-xs text-slate-500 font-ibm">
                    Example: Twilio, AWS SNS, Vonage, Plivo
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smsFromNumber" className="font-ibm font-medium">
                    From Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="smsFromNumber"
                    type="text"
                    value={smsFromNumber}
                    onChange={(e) => setSmsFromNumber(e.target.value)}
                    placeholder="+1234567890"
                    className="font-ibm"
                  />
                  <p className="text-xs text-slate-500 font-ibm">
                    Your SMS sender phone number (include country code)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="smsAccountSid" className="font-ibm font-medium">
                    Account SID / API Key
                  </Label>
                  <Input
                    id="smsAccountSid"
                    type="text"
                    value={smsAccountSid}
                    onChange={(e) => setSmsAccountSid(e.target.value)}
                    placeholder="Account SID or API Key"
                    className="font-ibm"
                  />
                  <p className="text-xs text-slate-500 font-ibm">
                    Your SMS provider account identifier
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smsAuthToken" className="font-ibm font-medium">
                    Auth Token / API Secret
                  </Label>
                  <Input
                    id="smsAuthToken"
                    type="password"
                    value={smsAuthToken}
                    onChange={(e) => setSmsAuthToken(e.target.value)}
                    placeholder="••••••••"
                    className="font-ibm"
                  />
                  <p className="text-xs text-slate-500 font-ibm">
                    Your SMS provider authentication token
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-ibm">
                  <strong>💡 Tip:</strong> For Twilio, find your Account SID and Auth Token in your Twilio Console dashboard. 
                  Make sure your phone number is verified and has SMS capabilities enabled.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 font-ibm">
                  <strong>⚠️ Note:</strong> SMS gateway integration is currently configured but not yet active. 
                  Future updates will enable sending actual SMS notifications for token activations and security alerts.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mi-Token API Tab */}
        <TabsContent value="mitoken" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit">Mi-Token API Configuration</CardTitle>
              <CardDescription className="font-ibm">
                Configure the Mi-Token API server and authentication key for token management
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4 p-6 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-900 font-ibm mb-1">
                      Important Configuration
                    </p>
                    <p className="text-sm text-amber-800 font-ibm">
                      Both fields are mandatory. The system will not be able to communicate with Mi-Token API without these settings.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="mitokenApiServer" className="font-ibm font-medium">
                    Mi-Token Server IP Address or Hostname <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="mitokenApiServer"
                    type="text"
                    value={mitokenApiServer}
                    onChange={(e) => setMitokenApiServer(e.target.value)}
                    placeholder="66.42.96.28 or mitoken.example.com"
                    className="font-ibm font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500 font-ibm">
                    Enter only the IP address or hostname (e.g., 66.42.96.28 or mitoken.example.com). The full API path will be constructed automatically.
                  </p>
                  <p className="text-xs text-blue-600 font-ibm mt-1">
                    → Full URL will be: https://{mitokenApiServer || '<server>'}/mi-token/api/authentication/v4/RawHttpWithSSL
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mitokenApiKey" className="font-ibm font-medium">
                    API Client Key <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="mitokenApiKey"
                    type="password"
                    value={mitokenApiKey}
                    onChange={(e) => setMitokenApiKey(e.target.value)}
                    placeholder="Enter your Mi-Token API client key"
                    className="font-ibm font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500 font-ibm">
                    Your unique API client key provided by Mi-Token (hexadecimal format)
                  </p>
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800 font-ibm">
                    <p className="font-semibold mb-1">How to configure:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li><strong>Server IP/Hostname:</strong> Enter only the IP address (e.g., 66.42.96.28) or hostname (e.g., mitoken.example.com)</li>
                      <li><strong>API Client Key:</strong> Request from your Mi-Token administrator (64-character hexadecimal)</li>
                      <li>The system will automatically construct the full API URL: https://&lt;server&gt;/mi-token/api/authentication/v4/RawHttpWithSSL</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Authentication Tab */}
        <TabsContent value="authentication" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit">Authentication Method</CardTitle>
              <CardDescription className="font-ibm">
                Choose how users will authenticate when logging into the user portal
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup value={loginType} onValueChange={setLoginType} className="space-y-4">
                {LOGIN_TYPES.map((type) => (
                  <div
                    key={type.value}
                    className={`relative flex items-start space-x-4 p-6 border-2 rounded-lg transition-all cursor-pointer ${
                      loginType === type.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    onClick={() => setLoginType(type.value)}
                  >
                    <RadioGroupItem value={type.value} id={type.value} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{type.icon}</span>
                        <Label
                          htmlFor={type.value}
                          className="font-ibm font-semibold text-lg text-slate-900 cursor-pointer"
                        >
                          {type.label}
                        </Label>
                        {type.recommended && (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                            Recommended
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 font-ibm leading-relaxed">
                        {type.description}
                      </p>
                    </div>
                    {loginType === type.value && (
                      <CheckCircle className="absolute top-6 right-6 h-6 w-6 text-blue-600" />
                    )}
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Floating Save Button */}
      <div className="sticky bottom-6 bg-white border border-slate-200 rounded-lg shadow-xl p-4 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {hasUnsavedChanges ? (
              <>
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <p className="text-sm text-slate-700 font-ibm font-medium">
                  You have unsaved changes
                </p>
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 text-emerald-600" />
                <p className="text-sm text-slate-600 font-ibm">
                  All changes saved
                </p>
              </>
            )}
          </div>
          <Button
            onClick={handleSaveSettings}
            disabled={saving || !hasUnsavedChanges}
            className="bg-emerald hover:bg-emerald/90 text-white font-ibm px-8"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {t('admin.settings.save')}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Test Email Dialog */}
      {showTestEmailDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold font-outfit">Send Test Email</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowTestEmailDialog(false);
                  setTestEmailAddress('');
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="testEmail" className="font-ibm font-medium">
                  Recipient Email Address
                </Label>
                <Input
                  id="testEmail"
                  type="email"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  placeholder="test@example.com"
                  className="font-ibm mt-2"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      sendTestEmail();
                    }
                  }}
                />
                <p className="text-xs text-slate-500 font-ibm mt-1">
                  Enter the email address where you want to receive the test email
                </p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowTestEmailDialog(false);
                    setTestEmailAddress('');
                  }}
                  className="flex-1 font-ibm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendTestEmail}
                  disabled={testingSmtp || !testEmailAddress}
                  className="flex-1 font-ibm"
                >
                  {testingSmtp ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Test
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
